import { useState } from "react";
import {
  CapturePlan,
  OutputVerdict,
  serverMessage,
  useCapturePlan,
  useCompareRunAgainstExpected,
  usePromoteRunToExpected,
} from "@via/api/process-testing.api.tsx";
import {
  BannerSize,
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner.tsx";
import Modal from "@via/components/modal.tsx";

/**
 * Every produced file the match dialog would capture for more than one port, with those ports in
 * the order the dialog lists them. A matched port counts as well as a chosen one, because picking
 * a file an already matched port captures from is the same slip (#555 item 1).
 *
 * Derived from the plan and the current choices on every render rather than kept in state, so it
 * can never disagree with the selects on screen.
 */
const filesChosenMoreThanOnce = (
  plan: CapturePlan,
  choices: Record<string, string>
): { file: string; ports: string[] }[] => {
  const portsByFile = new Map<string, string[]>();
  const bind = (port: string, file: string): void => {
    portsByFile.set(file, [...(portsByFile.get(file) ?? []), port]);
  };
  plan.captured.forEach(entry => bind(entry.port, entry.file));
  plan.skipped.forEach(entry => {
    const file = choices[entry.port] ?? "";
    if ("" !== file) bind(entry.port, file);
  });
  return [...portsByFile]
    .filter(([, ports]) => ports.length > 1)
    .map(([file, ports]) => ({ file, ports }));
};

const duplicateFileWarning = (
  duplicates: { file: string; ports: string[] }[]
): string => {
  const listed = duplicates
    .map(({ file, ports }) => `${file} (ports ${ports.join(", ")})`)
    .join("; ");
  return 1 === duplicates.length
    ? `The same file is selected for more than one output: ${listed}. Confirm only if that is intended.`
    : `Each of these files is selected for more than one output: ${listed}. Confirm only if that is intended.`;
};

/**
 * The most produced files one select lists (#555 item 3). A run publishing thousands of files made
 * each select unusable, so the list is bounded and the dialog says how many it left out.
 */
const MAX_LISTED_FILES = 100;

/** A list this short is easy to scan, so it gets no filter box. */
const FILTER_THRESHOLD = 20;

type ProducedFile = CapturePlan["produced"][number];

/**
 * The produced files one select offers: those whose path contains the filter (case insensitive),
 * at most `MAX_LISTED_FILES` of them. The chosen file is always kept, so a filter typed after a
 * choice can never leave the select showing a value it has no option for.
 */
const filesToList = (
  produced: ProducedFile[],
  filter: string,
  chosen: string
): { listed: ProducedFile[]; matching: number } => {
  const needle = filter.trim().toLowerCase();
  const matches = needle
    ? produced.filter(file => file.path.toLowerCase().includes(needle))
    : produced;
  const listed = matches.slice(0, MAX_LISTED_FILES);
  if ("" !== chosen && !listed.some(file => file.path === chosen)) {
    const kept = produced.find(file => file.path === chosen);
    if (kept) listed.unshift(kept);
  }
  return { listed, matching: matches.length };
};

const omittedFilesNote = (
  matching: number,
  filtered: boolean
): string | null => {
  if (0 === matching) return filtered ? "No file matches this filter." : null;
  if (matching <= MAX_LISTED_FILES) return null;
  const omitted = matching - MAX_LISTED_FILES;
  return filtered
    ? `Showing the first ${MAX_LISTED_FILES} of ${matching} matching files. ${omitted} are not listed; narrow the filter to find them.`
    : `Showing the first ${MAX_LISTED_FILES} of ${matching} files. ${omitted} are not listed; type in the filter to find them.`;
};

/**
 * The Test tab for a standalone process run.
 *
 * ⚠️ Wording is load bearing and constrained by spec 5.2. Capture records what the process
 * PRODUCED, not what it should produce, so a passing report says it matches what was captured and
 * never says "correct", "valid" or "validated". Overstating it is how a green tick starts being
 * read as scientific correctness.
 *
 * ⚠️ The tab is shown for every standalone process run, not only for one that already has
 * expected results. Gating it on "has expected results" would make Promote unreachable: you
 * cannot capture a baseline through a tab that only appears once a baseline exists. The empty
 * state is therefore an action, not a blank surface.
 */
export const RunTestTab = ({
  processId,
  runId,
}: {
  processId: number | null | undefined;
  /** A string on the run detail page, which reads it from the route. Only ever interpolated
   *  into the request URL, so widening is honest rather than a loosening. */
  runId: string | number | null | undefined;
}) => {
  const compare = useCompareRunAgainstExpected(processId, runId);
  const promote = usePromoteRunToExpected(processId, runId);
  const plan = useCapturePlan(processId, runId);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  /** The plan behind the open match dialog; null while the dialog is closed. */
  const [matchPlan, setMatchPlan] = useState<CapturePlan | null>(null);
  /** Chosen produced-file path per skipped port; "" means leave the port uncaptured. */
  const [choices, setChoices] = useState<Record<string, string>>({});
  /** Filter text per skipped port, so narrowing one port's list leaves the others alone. */
  const [filters, setFilters] = useState<Record<string, string>>({});

  const report = compare.data;
  const duplicates = matchPlan
    ? filesChosenMoreThanOnce(matchPlan, choices)
    : [];

  /**
   * Capture consults the plan first, so a port that would silently miss becomes a choice the
   * user makes by looking at the run's actual files. A failed plan falls back to the plain
   * capture on purpose: an older server without the endpoint keeps working, and every refusal
   * a promote can raise still surfaces through the promote error branch below.
   */
  const startCapture = () => {
    plan.mutate(undefined, {
      onSuccess: data => {
        // Nothing to choose between (every port matched), or nothing to choose FROM (the run
        // published no files at all). The second case would open a dialog whose only option is
        // "leave it uncaptured"; promote's own "produced no output files" refusal is the honest
        // answer, so both fall through to it.
        if (0 === data.skipped.length || 0 === data.produced.length) {
          promote.mutate();
          return;
        }
        setChoices({});
        setFilters({});
        setMatchPlan(data);
      },
      onError: () => promote.mutate(),
    });
  };

  const confirmMatchDialog = async (): Promise<void> => {
    const mapping = Object.entries(choices)
      .filter(([, file]) => "" !== file)
      .map(([port, file]) => ({ port, file }));
    promote.mutate(mapping.length ? { mapping } : undefined);
    setMatchPlan(null);
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="rounded bg-primary px-3 py-1.5 text-white disabled:opacity-50"
          disabled={compare.isPending}
          onClick={() => compare.mutate()}>
          {compare.isPending
            ? "Comparing…"
            : "Compare against expected results"}
        </button>
        <button
          type="button"
          className="rounded border px-3 py-1.5 disabled:opacity-50"
          disabled={promote.isPending || plan.isPending}
          onClick={startCapture}>
          {promote.isPending || plan.isPending
            ? "Capturing…"
            : "Capture this run as expected"}
        </button>
      </div>

      {matchPlan ? (
        <Modal
          open={true}
          setOpen={open => {
            if (!open) setMatchPlan(null);
          }}
          title="Match outputs to files"
          confirmText="Capture chosen files"
          // "Close" is the Modal's default and reads as "I am done here" next to a confirm that
          // writes. This dialog's other exit captures nothing, so it says so.
          cancelText="Cancel"
          // ⛔ Confirming with nothing captured and nothing chosen would post a plain promote,
          // whose refusal tells the reader to hand-author a glob in the yaml. That is exactly
          // the editing this dialog replaces, so the confirm is unavailable instead.
          isConfirmDisabled={
            0 === matchPlan.captured.length &&
            !Object.values(choices).some(file => "" !== file)
          }
          onConfirm={confirmMatchDialog}>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-gray-700">
              Some output ports did not match any of the files this run
              published. Choose which file each port should be captured from, or
              leave it out.
            </p>
            {matchPlan.captured.map(entry => (
              <div key={entry.port} className="flex flex-col">
                <p className="text-sm text-gray-700">
                  <span className="font-mono">{entry.port}</span> will be
                  captured from <span className="font-mono">{entry.file}</span>
                </p>
                {entry.note ? (
                  <p className="text-sm text-amber-800">{entry.note}</p>
                ) : null}
              </div>
            ))}
            {matchPlan.skipped.map(entry => {
              const chosen = choices[entry.port] ?? "";
              const filter = filters[entry.port] ?? "";
              const { listed, matching } = filesToList(
                matchPlan.produced,
                filter,
                chosen
              );
              const note = omittedFilesNote(matching, "" !== filter.trim());
              return (
                <div
                  key={entry.port}
                  className="flex flex-col gap-1 text-sm text-gray-700">
                  {/* The filter sits outside the label: a label names only its first control,
                      and the select must keep the port's reason as its name. */}
                  {matchPlan.produced.length > FILTER_THRESHOLD ? (
                    <input
                      type="search"
                      className="rounded border px-2 py-1"
                      placeholder="Filter files by path"
                      aria-label={`Filter files for ${entry.port}`}
                      value={filter}
                      onChange={event =>
                        setFilters(current => ({
                          ...current,
                          [entry.port]: event.target.value,
                        }))
                      }
                    />
                  ) : null}
                  <label className="flex flex-col gap-1">
                    <span>
                      <span className="font-mono">{entry.port}</span>{" "}
                      {entry.reason}
                    </span>
                    <select
                      className="rounded border px-2 py-1"
                      value={chosen}
                      onChange={event =>
                        setChoices(current => ({
                          ...current,
                          [entry.port]: event.target.value,
                        }))
                      }>
                      <option value="">Leave this port uncaptured</option>
                      {/* The size is shown because it is often the only thing distinguishing two
                          similarly named published files. */}
                      {listed.map(file => (
                        <option key={file.path} value={file.path}>
                          {file.path} ({file.bytes} bytes)
                        </option>
                      ))}
                    </select>
                  </label>
                  {note ? (
                    <p className="text-xs text-gray-500">{note}</p>
                  ) : null}
                </div>
              );
            })}
            {/* ⚠️ A warning, never a disabled confirm: one file really can serve two ports, so a
                duplicate may be exactly what the user means. Placed last, directly above the
                confirm, and announced as an alert so it is not only a colour change. */}
            {duplicates.length ? (
              <div role="alert">
                <MessageBanner
                  messages={duplicateFileWarning(duplicates)}
                  type={BannerType.Warning}
                  size={BannerSize.Small}
                />
              </div>
            ) : null}
          </div>
        </Modal>
      ) : null}

      <p className="text-sm text-gray-500">
        A passing comparison means this run matches the results captured for
        this process. It does not mean the results are scientifically correct.
      </p>

      {compare.isError ? (
        <p className="text-sm text-red-600">
          {serverMessage(
            compare.error,
            "The comparison could not be run. This process may have no expected results captured yet."
          )}
        </p>
      ) : null}

      {/* ⚠️ Capture refuses far more often than it fails, and both refusals are diagnoses the
          reader can act on: a run that published nothing, or a declared port that matched none of
          the published files. Without this branch the button appeared to do nothing at all, and
          the only way to learn why was the browser console. Found on staging while working the
          #498 acceptance row, which asks the tester to record a FAIL "on an error" and gave them
          no error to see. */}
      {promote.isError ? (
        <p className="text-sm text-red-600">
          {serverMessage(
            promote.error,
            "The run could not be captured as expected results."
          )}
        </p>
      ) : null}

      {promote.isSuccess ? (
        <div className="flex flex-col gap-1">
          <p className="text-sm text-green-700">
            Captured this run into {promote.data?.path}. Later runs are compared
            against it.
          </p>
          {promote.data?.captured?.map(entry => (
            <div key={entry.port} className="flex flex-col">
              <p className="text-sm text-gray-700">
                <span className="font-mono">{entry.port}</span> captured from{" "}
                <span className="font-mono">{entry.file}</span>
              </p>
              {/* ⚠️ A promote that rewrites the author's stated intent says so here, in the same
                  amber the degraded badge uses, because both mean "this is not quite what you
                  asked for". */}
              {entry.note ? (
                <p className="text-sm text-amber-800">{entry.note}</p>
              ) : null}
            </div>
          ))}
          {/* ⚠️ A port that matched nothing is absent from the captured document, so no later
              comparison will ever mention it. Without this list a partial capture reads as full
              coverage, which is exactly the silence #518 records. */}
          {promote.data?.skipped?.map(entry => (
            <p key={entry.port} className="text-sm text-amber-800">
              <span className="font-mono">{entry.port}</span> {entry.reason}
            </p>
          ))}
          {/* Waived ports are neutral, not a warning: nothing went wrong and the author already
              decided. Retained entries are amber, because a retained entry still grades and can
              fail the whole comparison. */}
          {promote.data?.waived?.map(entry => {
            // A reason that already ends in a period would otherwise read "..": trim one trailing
            // period before the fixed sentence below supplies its own.
            const reason = entry.reason.replace(/\.$/, "");
            return (
              <p key={entry.port} className="text-sm text-gray-700">
                <span className="font-mono">{entry.port}</span> is waived:{" "}
                {reason}. It was not captured.
              </p>
            );
          })}
          {promote.data?.retained?.map(entry => (
            <p key={entry.port} className="text-sm text-amber-800">
              <span className="font-mono">{entry.port}</span> {entry.reason}
            </p>
          ))}
        </div>
      ) : null}

      {report ? (
        <div className="flex flex-col gap-2">
          <p
            className={
              "pass" === report.status
                ? "font-medium text-green-700"
                : "font-medium text-red-600"
            }>
            {"pass" === report.status
              ? "Every output matches the results captured for this process."
              : "At least one output differs from the results captured for this process."}
          </p>

          {/* ⚠️ The headline above is about the entries the document HAS, so on a passing run it
              reads as full coverage even when a declared output has no entry at all. That run
              cannot be published: the ladder refuses it naming coverage, and without this block no
              screen anywhere says which output is missing. Same silence, and the same remedy, as
              the skipped list on a partial capture above. */}
          {report.coverage && "complete" !== report.coverage.status ? (
            <div className="flex flex-col gap-1">
              <p className="text-sm text-amber-800">
                {"empty" === report.coverage.status
                  ? "The expected results assert on none of this process's declared outputs, so this comparison tested nothing."
                  : "This process's expected results do not cover every declared output, so this run does not count as publication evidence."}
              </p>
              {report.coverage.uncoveredPorts.map(port => (
                <p key={port} className="text-sm text-amber-800">
                  <span className="font-mono">{port}</span> has no entry in the
                  expected results and is not waived.
                </p>
              ))}
            </div>
          ) : null}

          {report.outputs.map((output: OutputVerdict) => (
            <div key={output.port} className="rounded border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{output.port}</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs">
                  {output.comparator}
                </span>
                <span
                  className={
                    "pass" === output.status
                      ? "text-sm text-green-700"
                      : "text-sm text-red-600"
                  }>
                  {output.status}
                </span>
                {output.degraded ? (
                  <span
                    className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800"
                    title={`Compared by checksum because the output is ${output.degraded.bytes} bytes, over the ${output.degraded.cap} byte inline cap. Normalisation does not apply.`}>
                    compared by checksum, no normalisation
                  </span>
                ) : null}
              </div>

              <p className="mt-1 text-sm text-gray-700">{output.message}</p>

              {output.diff ? (
                <div className="mt-2">
                  <button
                    type="button"
                    className="text-sm underline"
                    onClick={() =>
                      setExpanded(current => ({
                        ...current,
                        [output.port]: !current[output.port],
                      }))
                    }>
                    {expanded[output.port]
                      ? "Hide difference"
                      : "Show difference"}
                  </button>
                  {expanded[output.port] ? (
                    <pre className="mt-2 max-h-96 overflow-auto rounded bg-gray-50 p-2 text-xs">
                      {output.diff}
                    </pre>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
